<script setup lang="ts">
/**
 * Медиа-блок карточки каталога: составная картинка комбо, одиночное фото/видео,
 * слайдер из нескольких кадров или плейсхолдер заведения.
 *
 * Вынесен из ProductCatalogSection, чтобы все пять шаблонов карточки показывали
 * медиа одинаково — иначе видео и комбо-сцены пришлось бы чинить в пяти местах.
 */
import { nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { ComboScene, comboSceneLayers } from '@entities/combo';
import {
  getCardImages,
  getCardBackgroundStyle,
  hasCardBackground,
  isVideoUrl,
  loadAlphaTrimBox,
  type AlphaTrimBox,
} from '@entities/product';
import { CARD_IMAGE_ASPECT_CSS, type CardImageAspect } from '@shared/config';

const props = withDefaults(
  defineProps<{
    item: any;
    aspect?: CardImageAspect;
    /** Плейсхолдер заведения (visual.placeholder_file) на случай товара без медиа. */
    placeholder?: string | null;
    /** `cover` — фото на всю карточку (шаблон overlay), `contain` — обычная плитка. */
    fill?: boolean;
    /** Карточка в первом экране: фото грузится сразу, а не по мере прокрутки. */
    priority?: boolean;
  }>(),
  { aspect: '1:1', placeholder: '', fill: false, priority: false },
);

const images = computed(() => getCardImages(props.item));

/** Слои составной картинки комбо. Пусто — комбо без расставленных слотов или не комбо. */
const sceneLayers = computed(() =>
  props.item?.type === 'combo' ? comboSceneLayers(props.item.groups) : [],
);

/**
 * Что можно отрисовать прямо на сервере.
 *
 * Раньше весь медиа-блок висел в <ClientOnly>, и в SSR-разметке главной не было
 * НИ ОДНОГО фото товара: поисковик не индексировал картинки меню, а браузер
 * узнавал про LCP-изображение только после гидрации.
 *
 * Обычная картинка (и плейсхолдер) — статичный <img>, он одинаков на сервере и
 * на клиенте, поэтому рендерим его сразу. В <ClientOnly> остаётся только то,
 * что без браузера не живёт: сцена комбо, видео и swiper-слайдер.
 */
const isStaticImage = computed(
  () =>
    sceneLayers.value.length === 0 &&
    images.value.length === 1 &&
    !isVideoUrl(images.value[0]?.url),
);

const isEmptyMedia = computed(
  () => sceneLayers.value.length === 0 && images.value.length === 0,
);

/**
 * Атрибуты загрузки картинки. Первые карточки первой категории тянем сразу и с
 * высоким приоритетом — это и есть LCP-элемент главной; остальное ленивое.
 */
const loadingAttrs = computed(() =>
  props.priority
    ? { loading: 'eager' as const, fetchpriority: 'high' as const }
    : { loading: 'lazy' as const, fetchpriority: 'auto' as const },
);

const aspectStyle = computed(() =>
  props.fill ? undefined : { aspectRatio: CARD_IMAGE_ASPECT_CSS[props.aspect] },
);

// ===== Блюдо на подложке: центр и максимальный размер без обрезки =====
//
// PNG блюда почти всегда приходит с широкими прозрачными полями, и обычный
// object-contain показывает товар мелким. По alpha-границам (loadAlphaTrimBox)
// картинка масштабируется так, чтобы видимая часть заняла максимум плитки,
// оставаясь целой: transform растит только прозрачные поля за края.

/** Доля плитки под блюдо — 2% воздуха с каждой стороны вместо прежнего padding 4%. */
const DISH_FILL = 0.96;

const mediaEl = ref<HTMLElement | null>(null);
const tileSize = ref<{ w: number; h: number } | null>(null);
let resizeObserver: ResizeObserver | null = null;

const measureTile = (el: HTMLElement) => {
  const rect = el.getBoundingClientRect();
  if (rect.width && rect.height) tileSize.value = { w: rect.width, h: rect.height };
};

const observeTile = (el: HTMLElement | null) => {
  resizeObserver?.disconnect();
  if (!el) return;
  // Первый замер — сразу: колбэк ResizeObserver приходит только с кадром
  // рендера, а плитка должна знать свой размер и в фоновой вкладке.
  measureTile(el);
  resizeObserver?.observe(el);
};

// Контейнер меняется вместе с ветками v-if (фото ↔ видео ↔ слайдер), а часть из них
// живёт внутри ClientOnly и появляется уже после маунта — вотчер обязан быть тут,
// на верхнем уровне setup.
watch(mediaEl, observeTile);

onMounted(() => {
  resizeObserver = new ResizeObserver((entries) => {
    const rect = entries[0]?.contentRect;
    if (rect?.width && rect?.height) tileSize.value = { w: rect.width, h: rect.height };
  });
  // Если контейнер успел отрисоваться до маунта, вотчер выше уже не сработает.
  observeTile(mediaEl.value);
});

onBeforeUnmount(() => resizeObserver?.disconnect());

const trimBoxes = reactive<Record<string, AlphaTrimBox | null>>({});

watch(
  images,
  (list) => {
    for (const media of list) {
      if (!hasCardBackground(props.item, media.url) || media.url in trimBoxes) continue;
      trimBoxes[media.url] = null;
      loadAlphaTrimBox(media.url).then((box) => {
        if (box) trimBoxes[media.url] = box;
      });
    }
  },
  { immediate: true },
);

/**
 * Инлайн-стиль для PNG на подложке. Пока границы не замерены (или замер
 * невозможен — CORS, битый файл) возвращает undefined, и работает прежний
 * CSS-фолбэк с padding и сдвигом вверх.
 */
const dishStyle = (url: string) => {
  const box = trimBoxes[url];
  const size = tileSize.value;
  if (!box || !size) return;

  // Размер картинки в плитке при object-contain без padding.
  const tileAspect = size.w / size.h;
  const containW = box.aspect > tileAspect ? size.w : size.h * box.aspect;
  const containH = box.aspect > tileAspect ? size.w / box.aspect : size.h;

  // Видимая часть блюда в этих координатах.
  const dishW = box.w * containW;
  const dishH = box.h * containH;
  if (!dishW || !dishH) return;

  const scale = DISH_FILL * Math.min(size.w / dishW, size.h / dishH);
  // Смещение центра блюда от центра плитки — после scale его гасит translate.
  const offsetX = (box.x + box.w / 2 - 0.5) * containW;
  const offsetY = (box.y + box.h / 2 - 0.5) * containH;

  return {
    padding: '0',
    objectPosition: 'center',
    transform: `translate(${(-scale * offsetX).toFixed(2)}px, ${(-scale * offsetY).toFixed(2)}px) scale(${scale.toFixed(4)})`,
  };
};

// ===== Видео в слайдере: играет только видимый кадр =====
const videoRefs = ref<Record<number, HTMLVideoElement | null>>({});
const activeSlide = ref(0);

const setVideoRef = (el: any, index: number) => {
  if (el) videoRefs.value[index] = el;
};

const onSlideChange = async (event: any) => {
  const swiper = event?.detail?.[0];
  if (!swiper) return;

  const previous = activeSlide.value;
  activeSlide.value = swiper.activeIndex;

  const previousVideo = videoRefs.value[previous];
  if (previousVideo) {
    previousVideo.pause();
    previousVideo.currentTime = 0;
  }

  await nextTick();
  const currentVideo = videoRefs.value[activeSlide.value];
  if (!currentVideo) return;
  try {
    currentVideo.currentTime = 0;
    await currentVideo.play();
  } catch {
    // Автовоспроизведение может быть заблокировано браузером — не наша забота.
  }
};
</script>

<template>
  <!-- Обычное фото товара: разметка одинакова на сервере и в браузере, поэтому
       рендерим сразу — иначе картинок меню нет ни в индексе, ни в LCP. -->
  <div
    v-if="isStaticImage"
    ref="mediaEl"
    class="pcard-media"
    :class="{ 'pcard-media--fill': fill }"
    :style="[aspectStyle, getCardBackgroundStyle(item, images[0]?.url)]"
  >
    <!-- crossorigin: замер alpha-границ идёт CORS-запросом, а cors- и no-cors-
         записи кэша не делятся — без атрибута каждый PNG качался бы дважды.
         io.tasteok.ru отражает Origin (проверено, включая https://localhost
         WebView), так что видимой картинке атрибут не вредит. -->
    <img
      :src="images[0].url"
      :alt="item.name"
      :loading="loadingAttrs.loading"
      :fetchpriority="loadingAttrs.fetchpriority"
      decoding="async"
      crossorigin="anonymous"
      class="pcard-media__el"
      :class="{ 'pcard-media__el--on-background': hasCardBackground(item, images[0]?.url) }"
      :style="dishStyle(images[0].url)"
    />
  </div>

  <!-- Плейсхолдер заведения: тоже статичная картинка, тоже уходит в SSR -->
  <div
    v-else-if="isEmptyMedia"
    class="pcard-media"
    :class="{ 'pcard-media--fill': fill }"
    :style="aspectStyle"
  >
    <!-- Плейсхолдер тоже бывает LCP: у первой категории каталога (часто это
         комбо) своих фото нет, и в первом экране лежит именно он. Без
         fetchpriority он вставал в общую очередь загрузки. -->
    <img
      v-if="placeholder"
      :src="placeholder"
      :alt="item.name"
      :loading="loadingAttrs.loading"
      :fetchpriority="loadingAttrs.fetchpriority"
      decoding="async"
      class="pcard-media__el"
    />
  </div>

  <!-- Дальше только то, что без браузера не собрать: сцена комбо, видео и swiper -->
  <ClientOnly v-else>
    <!-- Комбо с расставленными слотами: плитка собирается из позиций набора -->
    <div v-if="sceneLayers.length > 0" class="pcard-media" :class="{ 'pcard-media--fill': fill }" :style="aspectStyle">
      <ComboScene :layers="sceneLayers" class="absolute inset-0" />
    </div>

    <!-- Одиночное видео -->
    <div
      v-else-if="images.length === 1"
      ref="mediaEl"
      class="pcard-media"
      :class="{ 'pcard-media--fill': fill }"
      :style="[aspectStyle, getCardBackgroundStyle(item, images[0]?.url)]"
    >
      <video
        :src="images[0].url"
        playsinline
        muted
        loop
        autoplay
        preload="metadata"
        class="pcard-media__el"
      />
    </div>

    <!-- Несколько кадров — слайдер -->
    <div
      v-else
      ref="mediaEl"
      class="pcard-media pcard-media--slider"
      :class="{ 'pcard-media--fill': fill }"
      :style="aspectStyle"
    >
      <swiper-container
        :space-between="10"
        :slides-per-view="1"
        :loop="false"
        :pagination="{ clickable: true }"
        class="pcard-media__swiper"
        @swiperslidechange="onSlideChange"
        @click.stop
      >
        <swiper-slide v-for="(media, index) in images" :key="media.key || index" class="w-full h-full">
          <div class="absolute inset-0" :style="getCardBackgroundStyle(item, media.url)">
            <video
              v-if="isVideoUrl(media.url)"
              :ref="(el) => setVideoRef(el, Number(index))"
              :src="media.url"
              playsinline
              muted
              loop
              preload="metadata"
              class="pcard-media__el"
            />
            <img
              v-else
              :src="media.url"
              :alt="item.name"
              loading="lazy"
              crossorigin="anonymous"
              class="pcard-media__el"
              :class="{ 'pcard-media__el--on-background': hasCardBackground(item, media.url) }"
              :style="dishStyle(media.url)"
            />
          </div>
        </swiper-slide>
      </swiper-container>
    </div>
  </ClientOnly>
</template>

<style scoped lang="scss">
.pcard-media {
  @apply relative w-full flex-shrink-0 overflow-hidden;
  // Скругление приходит из конфига внешнего вида (shared/config/appearance)
  border-radius: var(--app-radius-media, 16px);
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.02), rgba(0, 0, 0, 0.05));

  &--fill {
    @apply absolute inset-0 h-full;
    border-radius: inherit;
  }

  &__el {
    @apply w-full h-full object-cover object-center;
    transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);

    /**
     * У товара есть своя подложка — значит сверху лежит PNG с прозрачностью,
     * и кадрировать его нельзя: `cover` срезал бы края блюда.
     *
     * `contain` вписывает картинку целиком в любую плитку и сохраняет её
     * собственные пропорции (у таких PNG они квадратные). Задавать
     * aspect-ratio вручную нельзя: при height 100% в неквадратной плитке
     * элемент становится шире контейнера, и overflow срезает ровно те края,
     * которые мы спасаем.
     *
     * Всё ниже — фолбэк на время замера alpha-границ (и на случай, когда
     * замер невозможен): как только dishStyle посчитан, инлайн-стиль
     * перекрывает padding и object-position, а блюдо центрируется
     * трансформом по своим реальным границам.
     */
    &--on-background {
      @apply object-contain;
      /*
       * Замеренный transform приходит асинхронно и по одному на карточку:
       * с унаследованным transition блюда «дозумливались» в случайные моменты
       * по мере завершения сканов. Первый и единственный transform — не анимация.
       */
      transition: none;
      /* Небольшой воздух, чтобы блюдо не упиралось в границы подложки */
      padding: 4%;
      /*
       * Свободное место делим не поровну: снизу карточки лежат название и
       * цена, и по геометрическому центру блюдо заезжает под них. Сдвигаем
       * выше — так оно попадает в оптический центр свободной части.
       */
      object-position: center 15%;
    }
  }

  &--slider {
    --swiper-pagination-color: var(--app-accent, #ef4444);
    --swiper-pagination-bullet-inactive-color: #9ca3af;
    --swiper-pagination-bullet-inactive-opacity: 0.5;
    --swiper-pagination-bullet-size: 7px;
    --swiper-pagination-bullet-horizontal-gap: 5px;
    --swiper-pagination-bottom: 10px;

    @media (max-width: 768px) {
      --swiper-pagination-bullet-size: 6px;
      --swiper-pagination-bullet-horizontal-gap: 4px;
      --swiper-pagination-bottom: 8px;
    }
  }

  &__swiper {
    @apply absolute inset-0 w-full h-full overflow-hidden;
    border-radius: inherit;
  }
}
</style>
